import { Role } from '@/types/Role'
import { Daycare, DaycareType } from '@/types/Daycare'
import { FunctionComponent, useEffect, useState } from 'react'
import styles from '../header/header.module.scss'
import { SelectOptionsDeprecated } from '@/types/Select'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { GET_ACCOUNT } from '@/modules/API/account/accountHandler'
import { useLazyQuery } from '@apollo/client'
import Desktop from '@/modules/desktopMobile/desktop'
import Mobile from '@/modules/desktopMobile/mobile'
import { GET_CHILD_WITHOUT_SECTION } from '@/modules/API/daycare/sectionHandler'
import DaycareSelector from '@/components/DaycareSelector/daycareSelector'
import { useTranslation } from 'react-i18next'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { Child } from '@/types/Child'
import { Section } from '@/types/Section'
import { Parent } from '@/types/Parent'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { Draw } from '@mui/icons-material'
import { NavigateFunction, useNavigate } from 'react-router-dom'

interface NewsHeaderProps {
    role: Role
    currentDaycare?: Daycare
    sectionIds: Array<number>
    setSectionIds: (sectionIds: Array<number>) => void
    childIds: Array<number>
    setChildIds: (childIds: Array<number>) => void
    canReadNewsFeed: boolean
    withoutSection?: boolean
    setWithoutSection?: (withoutSection: boolean) => void
    haveManyDaycares: boolean
    countDraftPosts: number
}

const NewsHeader: FunctionComponent<NewsHeaderProps> = ({
    role,
    currentDaycare,
    sectionIds,
    setSectionIds,
    childIds,
    setChildIds,
    canReadNewsFeed,
    withoutSection,
    setWithoutSection,
    haveManyDaycares,
    countDraftPosts
}: NewsHeaderProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [getAccount] = useLazyQuery(GET_ACCOUNT)
    const [childs, setChilds] = useState<Array<Child>>([])

    const [showWithoutSection, setShowWithoutSection] = useState<boolean>(false)
    const [sectionIdsString, setSectionIdsString] = useState<string[]>([])

    const isRpeOrLaep: boolean = currentDaycare?.type === DaycareType.rpe || currentDaycare?.type === DaycareType.laep

    const sectionOptions: SelectOptionsDeprecated = [
        ...[
            {
                options: currentDaycare?.sections
                    ? currentDaycare?.sections?.map((section) => {
                          return {
                              name: section.name,
                              value: section.id.toString()
                          }
                      })
                    : []
            }
        ]
    ]

    const sectionOptionsWithoutSection: SelectOptionsDeprecated = [
        ...[
            {
                options: currentDaycare?.sections
                    ? currentDaycare?.sections?.map((section) => {
                          return {
                              name: section.name,
                              value: section.id.toString()
                          }
                      })
                    : []
            }
        ],
        ...[
            {
                options: [
                    {
                        name: isRpeOrLaep ? t('withoutSector') : t('withoutSection'),
                        value: 'withoutSection'
                    }
                ]
            }
        ]
    ]

    const [getChildWithoutSection, { data: dataChildWithoutSection }] = useLazyQuery<{
        getChildWithoutSection: Array<number>
    }>(GET_CHILD_WITHOUT_SECTION, {
        variables: {
            daycare_id: currentDaycare?.id
        }
    })

    useEffect(() => {
        if (dataChildWithoutSection && dataChildWithoutSection.getChildWithoutSection.length > 0) {
            setShowWithoutSection(true)
            setSectionIdsString([
                ...sectionIds.map((id: number) => id.toString()),
                ...(withoutSection ? ['withoutSection'] : [])
            ])
        } else {
            setSectionIdsString([...sectionIds.map((id: number) => id.toString())])
        }
    }, [dataChildWithoutSection])

    function setSection(newValue: string[]): void {
        setSectionIdsString(newValue)
        const newSectionIds: number[] = newValue
            .filter((id: string): boolean => id !== 'withoutSection')
            .map((id: string) => Number(id))
        setSectionIds(newSectionIds)
        if (newValue.includes('withoutSection')) {
            if (setWithoutSection) setWithoutSection(true)
        } else {
            if (setWithoutSection) setWithoutSection(false)
        }
    }

    function setChild(newValue: string[]): void {
        setChildIds(newValue.map((id) => Number(id)))
    }

    const childOptions: SelectOptionsDeprecated = [
        {
            options: childs.map((child: Child) => {
                return {
                    name: child.firstname,
                    value: child.id.toString()
                }
            })
        }
    ]

    useEffect(() => {
        if (role === Role.staff && currentDaycare && currentDaycare.sections) {
            setSectionIds(currentDaycare.sections.map((section: Section) => section.id as number))
        }

        if (role === Role.parent) {
            getAccount().then((r) => {
                const account: Parent = r.data?.getAccount
                if (account && account.childs) {
                    setChilds(account.childs)
                    setChildIds(account.childs.map((child: { id: number }) => child.id as number))
                }
            })
        } else {
            getChildWithoutSection()
        }
    }, [])

    function onSectionChange(sectionId: number): void {
        if (sectionIds.includes(sectionId)) {
            setSectionIds(sectionIds.filter((id: number): boolean => id !== sectionId))
        } else {
            setSectionIds([...sectionIds, sectionId])
        }
    }

    function onChildChange(childId: number): void {
        if (childIds.includes(childId)) {
            setChildIds(childIds.filter((id: number): boolean => id !== childId))
        } else {
            setChildIds([...childIds, childId])
        }
    }

    return (
        <div className={styles.HeaderRow}>
            {haveManyDaycares && (
                <div className={styles.MultipleSections}>
                    <DaycareSelector />
                </div>
            )}
            <Desktop>
                {role === Role.staff &&
                    canReadNewsFeed &&
                    currentDaycare &&
                    currentDaycare.sections &&
                    currentDaycare?.sections?.length > 0 && (
                        <div className={styles.HeaderRowElement}>
                            <p className={`${styles.FilterTitle} ${styles.SectionFilter}`}>
                                {isRpeOrLaep ? t('mySectors') : t('mySections')}
                            </p>
                            {currentDaycare.sections.map((section, index) => {
                                return (
                                    <div key={index} className={styles.sectionCheckbox}>
                                        <Checkbox
                                            text={section.name.toString()}
                                            checked={sectionIds.includes(section.id as number)}
                                            onChange={() => onSectionChange(section.id as number)}
                                            classText={styles.sectionCheckboxText}
                                        />
                                    </div>
                                )
                            })}
                            {showWithoutSection && dataChildWithoutSection && (
                                <div className={styles.sectionCheckbox}>
                                    <Checkbox
                                        text={isRpeOrLaep ? t('withoutSector') : t('withoutSection')}
                                        checked={withoutSection}
                                        onChange={() => (setWithoutSection ? setWithoutSection(!withoutSection) : null)}
                                        classText={styles.sectionCheckboxText}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                {role === Role.parent && childs.length > 0 && (
                    <div className={styles.HeaderRowElement}>
                        <p className={`${styles.FilterTitle} ${styles.ChildFilter}`}>{t('myChilds')}</p>
                        {childs.map((child, index) => {
                            return (
                                <div key={index} className={styles.sectionCheckbox}>
                                    <Checkbox
                                        text={child.firstname.toString()}
                                        checked={childIds.includes(child.id as number)}
                                        onChange={() => onChildChange(child.id as number)}
                                        classText={styles.sectionCheckboxText}
                                        parent={true}
                                    />
                                </div>
                            )
                        })}
                    </div>
                )}
            </Desktop>
            <Mobile>
                {role === Role.staff &&
                    canReadNewsFeed &&
                    currentDaycare &&
                    currentDaycare.sections &&
                    currentDaycare?.sections?.length > 0 && (
                        <div className={`${styles.HeaderRowElement} ${styles.alignLeft} ${styles.FilterContainer}`}>
                            <SelectMultiple
                                label={
                                    currentDaycare?.type === DaycareType.rpe ||
                                    currentDaycare?.type === DaycareType.laep
                                        ? t('mySectors')
                                        : t('mySections')
                                }
                                selectOptions={showWithoutSection ? sectionOptionsWithoutSection : sectionOptions}
                                selectValues={sectionIdsString}
                                setSelectValues={setSection}
                                className={styles.SelectMultiple}
                                fullWidth={true}
                            />
                        </div>
                    )}
                {role === Role.parent && childs.length > 0 && (
                    <div className={`${styles.HeaderRowElement} ${styles.alignLeft} ${styles.FilterContainer}`}>
                        <SelectMultiple
                            label={t('childs')}
                            selectOptions={childOptions}
                            selectValues={childIds.map((id) => id.toString())}
                            setSelectValues={setChild}
                            className={styles.SelectMultiple}
                            fullWidth={true}
                            isParent={true}
                        />
                    </div>
                )}
            </Mobile>
            {role !== Role.parent && countDraftPosts > 0 && (
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<Draw />}
                        customText={t('showDraft')}
                        onClick={() => navigate('/news/draft')}
                        badge={countDraftPosts}
                        fullWidth
                    />
                </div>
            )}
        </div>
    )
}

export default NewsHeader
